/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : Modal
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : THE canonical modal. One behavior for the whole portal:
 *                  movable (drag the header), transparent-but-BLOCKING backdrop
 *                  (the page stays visible behind it), frame + shadow, and
 *                  outside-click does NOT dismiss (close via the ✕, Escape, or
 *                  an explicit action). Supersedes the hand-rolled
 *                  `fixed inset-0` modals, AppModal (MUI), and operations/
 *                  DetailModal — new modals MUST use this.
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 */
import React, { useEffect, useId, useRef } from 'react';
import { X } from 'lucide-react';
import { clsx } from 'clsx';
import { useDraggable } from './useDraggable';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  /** Optional sticky footer (e.g. action buttons). */
  footer?: React.ReactNode;
  size?: ModalSize;
  /** Hide the ✕ (e.g. a wizard that controls its own close). Default false. */
  hideCloseButton?: boolean;
  className?: string;
}

/** What Tab can reach inside the dialog (the controls a keyboard user moves between). */
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const sizeClass: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  full: 'max-w-[95vw]',
};

/** Canonical modal — see the design blueprint before building another. */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  hideCloseButton = false,
  className,
}) => {
  // Movable by the header; drags ignore interactive controls (the close button).
  const { transform, onHandleMouseDown } = useDraggable({ handleSelector: '[data-modal-drag-handle]' });
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // The WAI-ARIA modal dialog pattern (w3.org/WAI/ARIA/apg/patterns/dialog-modal): focus moves inside when the dialog
  // opens (to the panel, so the dialog's name is announced and Enter cannot fire a control by accident; the next Tab
  // reaches the first control), and returns to the element that opened it when it closes.
  useEffect(() => {
    if (!isOpen) return undefined;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    return () => {
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [isOpen]);

  // Tab and Shift+Tab stay inside the dialog: from the last control Tab wraps to the first, and back.
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const tabbable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(TABBABLE));
    if (tabbable.length === 0) {
      e.preventDefault();
      return;
    }
    const first = tabbable[0];
    const last = tabbable[tabbable.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panelRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      onKeyDown={onKeyDown}
    >
      {/* Scrim: dims the page just enough that the modal clearly overlays
          everything, while you still see behind it. Stronger in dark — a faint
          scrim over an already-dark page wouldn't separate the modal. Clicking
          it does NOT close (portal-wide rule). */}
      <div className="fixed inset-0 bg-black/15 dark:bg-black/45" aria-hidden="true" />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={panelRef}
          tabIndex={-1}
          onMouseDown={onHandleMouseDown}
          style={{ transform }}
          className={clsx(
            'relative w-full overflow-hidden rounded-[var(--radius-lg)]',
            'bg-[var(--color-bg-card)] text-[var(--color-text-primary)]',
            'border-2 border-[var(--color-border)] shadow-[var(--shadow-lg)]',
            // The panel takes focus when the dialog opens only so the dialog's name is announced; it is not a control, so it
            // draws no focus ring (Chrome showed one even after a mouse click, framing every modal). The controls inside keep
            // theirs, and the next Tab reaches the first of them.
            'outline-none',
            sizeClass[size],
            className,
          )}
        >
          {(title || !hideCloseButton) && (
            <div
              data-modal-drag-handle
              className="flex cursor-move select-none items-center justify-between border-b border-[var(--color-border)] px-6 py-4"
            >
              <h3 id={titleId} className="text-lg font-semibold text-[var(--color-text-primary)]">
                {title}
              </h3>
              {!hideCloseButton && (
                <button
                  onClick={onClose}
                  aria-label="Close"
                  className="text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          )}

          <div className="max-h-[calc(100vh-220px)] overflow-y-auto px-6 py-4">{children}</div>

          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border)] px-6 py-3">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
